import { useState, useRef } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import * as XLSX from "xlsx";
import { DashboardLayout } from "@/components/dashboard/DashboardLayout";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Card, CardContent, CardHeader } from "@/components/ui/card";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Label } from "@/components/ui/label";
import { Plus, Pencil, Trash2, Search, Upload, Download, AlertTriangle } from "lucide-react";
import { toast } from "sonner";
import { fetchDrivers, createDriver, updateDriver, deleteDriver, Driver } from "@/lib/traccar-api";

interface ImportRow {
  name: string;
  uniqueId: string;
  itin?: string;
  phone?: string;
  address?: string;
}

export default function Drivers() {
  const { t } = useTranslation();
  const [search, setSearch] = useState("");
  const [isOpen, setIsOpen] = useState(false);
  const [editing, setEditing] = useState<Driver | null>(null);
  const [importOpen, setImportOpen] = useState(false);
  const [importRows, setImportRows] = useState<ImportRow[]>([]);
  const [isImporting, setIsImporting] = useState(false);
  const [fileParsed, setFileParsed] = useState(false);
  const [totalRows, setTotalRows] = useState(0);
  const [deleteAllOpen, setDeleteAllOpen] = useState(false);
  const [isDeletingAll, setIsDeletingAll] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const queryClient = useQueryClient();

  const { data: drivers = [], isLoading } = useQuery({
    queryKey: ["drivers"],
    queryFn: fetchDrivers,
  });

  const createMutation = useMutation({
    mutationFn: createDriver,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["drivers"] });
      setIsOpen(false);
      toast.success(t("drivers.created"));
    },
    onError: (error: Error) => {
      toast.error(error.message);
    },
  });

  const updateMutation = useMutation({
    mutationFn: updateDriver,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["drivers"] });
      setIsOpen(false);
      setEditing(null);
      toast.success(t("drivers.updated"));
    },
    onError: (error: Error) => {
      toast.error(error.message);
    },
  });

  const deleteMutation = useMutation({
    mutationFn: deleteDriver,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["drivers"] });
      toast.success(t("drivers.deleted"));
    },
    onError: (error: Error) => {
      toast.error(error.message);
    },
  });

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setFileParsed(false);
    setImportRows([]);
    setTotalRows(0);
    const reader = new FileReader();
    reader.onload = (evt) => {
      try {
        const data = evt.target?.result;
        const wb = XLSX.read(data, { type: "binary" });
        const ws = wb.Sheets[wb.SheetNames[0]];
        const rows = XLSX.utils.sheet_to_json<Record<string, string>>(ws, { defval: "" });
        setTotalRows(rows.length);
        const parsed: ImportRow[] = rows
          .map(r => ({
            name: String(r.name || r.Name || "").trim(),
            uniqueId: String(r.uniqueId || r.UniqueId || r["Unique ID"] || r.unique_id || "").trim(),
            itin: String(r.itin || r["ID Number"] || r.identification_number || "").trim() || undefined,
            phone: String(r.phone || r.Phone || "").trim() || undefined,
            address: String(r.address || r.Address || "").trim() || undefined,
          }))
          .filter(r => r.name && r.uniqueId);
        setImportRows(parsed);
        setFileParsed(true);
      } catch {
        setFileParsed(true);
        toast.error(t("drivers.importReadError"));
      }
    };
    reader.onerror = () => {
      setFileParsed(true);
      toast.error(t("drivers.importReadError"));
    };
    reader.readAsBinaryString(file);
  };

  const handleImport = async () => {
    if (importRows.length === 0) return;
    setIsImporting(true);
    let success = 0;
    let failed = 0;
    for (const row of importRows) {
      try {
        await createDriver({
          name: row.name,
          uniqueId: row.uniqueId,
          attributes: {
            itin: row.itin,
            phone: row.phone,
            address: row.address,
          },
        });
        success++;
      } catch {
        failed++;
      }
    }
    setIsImporting(false);
    queryClient.invalidateQueries({ queryKey: ["drivers"] });
    setImportOpen(false);
    setImportRows([]);
    if (fileInputRef.current) fileInputRef.current.value = "";
    if (failed === 0) {
      toast.success(t("drivers.importSuccess", { count: success }));
    } else {
      toast.warning(t("drivers.importPartial", { success, failed }));
    }
  };

  const handleDeleteAll = async () => {
    setIsDeletingAll(true);
    let failed = 0;
    for (const driver of drivers) {
      try {
        await deleteDriver(driver.id);
      } catch {
        failed++;
      }
    }
    setIsDeletingAll(false);
    setDeleteAllOpen(false);
    queryClient.invalidateQueries({ queryKey: ["drivers"] });
    if (failed === 0) {
      toast.success(t("drivers.deleteAllSuccess", { count: drivers.length }));
    } else {
      toast.warning(t("drivers.deleteAllPartial", { failed }));
    }
  };

  const downloadTemplate = () => {
    const ws = XLSX.utils.aoa_to_sheet([
      ["name", "uniqueId", "itin", "phone", "address"],
      ["John Doe", "123456", "AB123456", "+351912345678", "Rua Example 1"],
    ]);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, "Drivers");
    XLSX.writeFile(wb, "drivers_template.xlsx");
  };

  const filtered = drivers.filter(
    (d) => {
      const q = search.toLowerCase();
      return (
        d.name.toLowerCase().includes(q) ||
        d.uniqueId.toLowerCase().includes(q) ||
        ((d.attributes?.itin as string) || "").toLowerCase().includes(q) ||
        ((d.attributes?.phone as string) || "").toLowerCase().includes(q) ||
        ((d.attributes?.address as string) || "").toLowerCase().includes(q)
      );
    }
  );

  const handleSubmit = (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const formData = new FormData(e.currentTarget);
    const attributes: Record<string, unknown> = {
      ...(editing?.attributes || {}),
      itin: (formData.get("itin") as string) || undefined,
      phone: (formData.get("phone") as string) || undefined,
      address: (formData.get("address") as string) || undefined,
    };
    const data = {
      name: formData.get("name") as string,
      uniqueId: formData.get("uniqueId") as string,
      attributes,
    };

    if (editing) {
      updateMutation.mutate({ ...data, id: editing.id });
    } else {
      createMutation.mutate(data);
    }
  };

  return (
    <DashboardLayout>
      <div className="p-6 space-y-6">
        <div className="flex items-center justify-between">
          <h1 className="text-3xl font-bold">{t("drivers.title")}</h1>
          <div className="flex items-center gap-2">
            {/* Import Dialog */}
            <Dialog open={importOpen} onOpenChange={(open) => { setImportOpen(open); if (!open) { setImportRows([]); setFileParsed(false); setTotalRows(0); if (fileInputRef.current) fileInputRef.current.value = ""; } }}>
              <DialogTrigger asChild>
                <Button variant="outline"><Upload className="h-4 w-4 mr-2" /> {t("drivers.import")}</Button>
              </DialogTrigger>
              <DialogContent className="max-w-3xl">
                <DialogHeader>
                  <DialogTitle>{t("drivers.importTitle")}</DialogTitle>
                </DialogHeader>
                <div className="space-y-4">
                  <div className="flex items-center gap-3">
                    <Button variant="outline" size="sm" onClick={downloadTemplate}>
                      <Download className="h-4 w-4 mr-2" /> {t("drivers.downloadTemplate")}
                    </Button>
                    <span className="text-sm text-muted-foreground">{t("drivers.importHint")}</span>
                  </div>
                  <input
                    ref={fileInputRef}
                    type="file"
                    accept=".csv,.xlsx,.xls"
                    onChange={handleFileChange}
                    className="block w-full text-sm text-muted-foreground file:mr-4 file:py-2 file:px-4 file:rounded-md file:border-0 file:text-sm file:font-medium file:bg-primary file:text-primary-foreground hover:file:bg-primary/90 cursor-pointer"
                  />
                  {fileParsed && importRows.length === 0 && (
                    <p className="text-sm text-destructive">
                      {totalRows === 0
                        ? t("drivers.importEmptyFile")
                        : t("drivers.importNoValidRows", { count: totalRows })}
                    </p>
                  )}
                  {importRows.length > 0 && (
                    <>
                      <p className="text-sm font-medium">{t("drivers.importPreview", { count: importRows.length })}</p>
                      <div className="max-h-64 overflow-y-auto border rounded-md">
                        <Table>
                          <TableHeader>
                            <TableRow>
                              <TableHead>{t("drivers.name")}</TableHead>
                              <TableHead>{t("drivers.uniqueId")}</TableHead>
                              <TableHead>{t("drivers.itin")}</TableHead>
                              <TableHead>{t("drivers.phone")}</TableHead>
                              <TableHead>{t("drivers.address")}</TableHead>
                            </TableRow>
                          </TableHeader>
                          <TableBody>
                            {importRows.map((row, i) => (
                              <TableRow key={i}>
                                <TableCell>{row.name}</TableCell>
                                <TableCell className="font-mono text-sm">{row.uniqueId}</TableCell>
                                <TableCell>{row.itin || "—"}</TableCell>
                                <TableCell>{row.phone || "—"}</TableCell>
                                <TableCell>{row.address || "—"}</TableCell>
                              </TableRow>
                            ))}
                          </TableBody>
                        </Table>
                      </div>
                      <Button onClick={handleImport} disabled={isImporting} className="w-full">
                        {isImporting ? t("common.loading") : t("drivers.importConfirm", { count: importRows.length })}
                      </Button>
                    </>
                  )}
                </div>
              </DialogContent>
            </Dialog>

            {/* Delete All Dialog */}
            <Dialog open={deleteAllOpen} onOpenChange={setDeleteAllOpen}>
              <DialogTrigger asChild>
                <Button variant="destructive" disabled={drivers.length === 0}><Trash2 className="h-4 w-4 mr-2" /> {t("drivers.deleteAll")}</Button>
              </DialogTrigger>
              <DialogContent className="max-w-sm">
                <DialogHeader>
                  <DialogTitle className="flex items-center gap-2"><AlertTriangle className="h-5 w-5 text-destructive" /> {t("drivers.deleteAllTitle")}</DialogTitle>
                </DialogHeader>
                <p className="text-sm text-muted-foreground">{t("drivers.deleteAllConfirm", { count: drivers.length })}</p>
                <div className="flex gap-2 mt-2">
                  <Button variant="outline" className="flex-1" onClick={() => setDeleteAllOpen(false)}>{t("common.cancel")}</Button>
                  <Button variant="destructive" className="flex-1" onClick={handleDeleteAll} disabled={isDeletingAll}>
                    {isDeletingAll ? t("common.loading") : t("drivers.deleteAll")}
                  </Button>
                </div>
              </DialogContent>
            </Dialog>

            <Dialog open={isOpen} onOpenChange={(open) => { setIsOpen(open); if (!open) setEditing(null); }}>
              <DialogTrigger asChild>
                <Button><Plus className="h-4 w-4 mr-2" /> {t("drivers.add")}</Button>
              </DialogTrigger>
            <DialogContent className="max-w-md">
              <DialogHeader>
                <DialogTitle>{editing ? t("drivers.edit") : t("drivers.add")}</DialogTitle>
              </DialogHeader>
              <form onSubmit={handleSubmit} className="space-y-4">
                <div className="space-y-2">
                  <Label htmlFor="name">{t("drivers.name")} *</Label>
                  <Input id="name" name="name" required defaultValue={editing?.name} />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="uniqueId">{t("drivers.uniqueId")} *</Label>
                  <Input id="uniqueId" name="uniqueId" required defaultValue={editing?.uniqueId} />
                  <p className="text-xs text-muted-foreground">{t("drivers.uniqueIdHint")}</p>
                </div>
                <div className="space-y-2">
                  <Label htmlFor="itin">{t("drivers.itin")}</Label>
                  <Input id="itin" name="itin" defaultValue={(editing?.attributes?.itin as string) || ""} />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="phone">{t("drivers.phone")}</Label>
                  <Input id="phone" name="phone" type="tel" defaultValue={(editing?.attributes?.phone as string) || ""} />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="address">{t("drivers.address")}</Label>
                  <Input id="address" name="address" defaultValue={(editing?.attributes?.address as string) || ""} />
                </div>
                <Button type="submit" className="w-full" disabled={createMutation.isPending || updateMutation.isPending}>
                  {editing ? t("common.update") : t("common.create")}
                </Button>
              </form>
            </DialogContent>
            </Dialog>
          </div>
        </div>

        <Card>
          <CardHeader>
            <div className="flex items-center justify-between">
            <div className="relative max-w-sm">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
              <Input placeholder={t("drivers.searchPlaceholder")} value={search} onChange={(e) => setSearch(e.target.value)} className="pl-9" />
            </div>
            <span className="text-sm text-muted-foreground">{t("drivers.total", { count: drivers.length })}</span>
            </div>
          </CardHeader>
          <CardContent>
            {isLoading ? (
              <div className="text-center py-8 text-muted-foreground">{t("common.loading")}</div>
            ) : filtered.length === 0 ? (
              <div className="text-center py-8 text-muted-foreground">{t("drivers.noDrivers")}</div>
            ) : (
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>{t("drivers.name")}</TableHead>
                    <TableHead>{t("drivers.uniqueId")}</TableHead>
                    <TableHead>{t("drivers.itin")}</TableHead>
                    <TableHead>{t("drivers.phone")}</TableHead>
                    <TableHead>{t("drivers.address")}</TableHead>
                    <TableHead className="w-[100px]">{t("reseller.common.actions")}</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {filtered.map((driver) => (
                    <TableRow key={driver.id}>
                      <TableCell className="font-medium">{driver.name}</TableCell>
                      <TableCell className="font-mono text-sm">{driver.uniqueId}</TableCell>
                      <TableCell>{(driver.attributes?.itin as string) || "—"}</TableCell>
                      <TableCell>{(driver.attributes?.phone as string) || "—"}</TableCell>
                      <TableCell>{(driver.attributes?.address as string) || "—"}</TableCell>
                      <TableCell>
                        <div className="flex items-center gap-2">
                          <Button variant="ghost" size="icon" onClick={() => { setEditing(driver); setIsOpen(true); }}>
                            <Pencil className="h-4 w-4" />
                          </Button>
                          <Button variant="ghost" size="icon" onClick={() => { if (confirm(t("drivers.deleteConfirm"))) deleteMutation.mutate(driver.id); }}>
                            <Trash2 className="h-4 w-4" />
                          </Button>
                        </div>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            )}
          </CardContent>
        </Card>
      </div>
    </DashboardLayout>
  );
}
